import { ChevronDown, RefreshCw } from 'lucide-react';

import {
  GROUP_LABELS,
  GROUP_ORDER,
  type GroupKey,
} from '@/components/dev/feature-flags/feature-flags-groups';
import { type FlagOverrideValue } from '@/features/flags-dev/overrides';
import { Button } from '@heidi/design-system/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@heidi/design-system/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@heidi/design-system/tooltip';
import { TypographyCaption } from '@heidi/design-system/typography';

/** Acts on the CURRENTLY VISIBLE flags (search + "changed only" applied), not all of them. */
export const FeatureFlagsBulkActions = ({
  filteredCount,
  overrideCount,
  favouriteCount,
  onSetAll,
  onResetOverrides,
  onClearFavourites,
}: {
  filteredCount: number;
  overrideCount: number;
  favouriteCount: number;
  onSetAll: (value: FlagOverrideValue) => void;
  onResetOverrides: () => void;
  onClearFavourites: () => void;
}) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button
        type="button"
        variant="outline"
        size="xs"
        data-testid="dev-tools-feature-flags-bulk-actions"
      >
        Bulk actions
        {overrideCount > 0 ? ` (${overrideCount})` : ''}
        <ChevronDown size={14} aria-hidden />
      </Button>
    </DropdownMenuTrigger>
    {/* One above the dev sheet's z-[10000] so the dropdown isn't clipped. */}
    <DropdownMenuContent align="end" className="z-[10001]">
      <DropdownMenuItem
        onSelect={() => onSetAll('on')}
        disabled={filteredCount === 0}
        data-testid="dev-tools-feature-flags-set-all-on"
      >
        Set {filteredCount} visible to On
      </DropdownMenuItem>
      <DropdownMenuItem
        onSelect={() => onSetAll('off')}
        disabled={filteredCount === 0}
        data-testid="dev-tools-feature-flags-set-all-off"
      >
        Set {filteredCount} visible to Off
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem
        onSelect={onResetOverrides}
        disabled={overrideCount === 0}
        data-testid="dev-tools-feature-flags-reset-all"
      >
        Reset all overrides
        {overrideCount > 0 ? ` (${overrideCount})` : ''}
      </DropdownMenuItem>
      <DropdownMenuItem
        onSelect={onClearFavourites}
        disabled={favouriteCount === 0}
        data-testid="dev-tools-feature-flags-clear-favourites"
      >
        Clear all favourites
        {favouriteCount > 0 ? ` (${favouriteCount})` : ''}
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

export const FeatureFlagsJumpTo = ({
  counts,
  onJump,
  onRefresh,
}: {
  counts: Record<GroupKey, number>;
  onJump: (groupKey: GroupKey) => void;
  onRefresh: () => void;
}) => (
  <div className="mt-2 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
    <TypographyCaption className="text-text-secondary">Jump to:</TypographyCaption>
    <div className="flex flex-1 flex-wrap items-baseline gap-x-2 gap-y-1">
      {GROUP_ORDER.map((groupKey) => {
        const count = counts[groupKey];
        // A permanent disabled "Favourites (0)" link is just noise.
        if (groupKey === 'favourites' && count === 0) return null;
        return (
          <Button
            key={groupKey}
            type="button"
            variant="link"
            size="xs"
            flush="all"
            onClick={() => onJump(groupKey)}
            disabled={count === 0}
            className="text-text-secondary hover:text-text-primary"
            data-testid={`dev-tools-feature-flags-jump-${groupKey}`}
          >
            {GROUP_LABELS[groupKey]} ({count})
          </Button>
        );
      })}
    </div>
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="xxs"
          iconOnly
          onClick={onRefresh}
          className="text-text-secondary hover:text-text-primary [&_svg]:stroke-current"
          data-testid="dev-tools-feature-flags-refresh"
          aria-label="Refetch flags"
        >
          <RefreshCw aria-hidden />
        </Button>
      </TooltipTrigger>
      <TooltipContent className="z-[10001]">Refetch flags</TooltipContent>
    </Tooltip>
  </div>
);
